<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
import type { SiteBlock } from '~/types/types'
import { usePublicSiteRepository } from '~/repositories/publicSiteRepository'

defineProps<{ block: SiteBlock }>()

const repo = usePublicSiteRepository()

/**
 * Category choices come from the live catalogue, so the editor can only
 * pick a category that actually exists. Typing one by hand produced an
 * empty block with no explanation.
 */
const { data: priceList } = await useAsyncData('services-list-categories', () => repo.getPriceList())

const categories = computed(() => [
  { label: 'All categories', value: '' },
  ...(priceList.value?.data?.groups ?? [])
    .filter(g => !!g.category)
    .map(g => ({ label: g.category as string, value: g.category as string }))
])

const hasCatalogue = computed(() =>
  (priceList.value?.data?.groups ?? []).some(g => g.items.length > 0))
</script>

<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <p
    v-if="!hasCatalogue"
    class="text-sm ui-text-subtle"
  >
    Your price list is empty — this block will show nothing until you add
    services under Price list.
  </p>

  <UFormField label="Title">
    <UInput
      v-model="block.data.title"
      class="w-full"
      placeholder="Үнийн мэдээлэл"
    />
  </UFormField>

  <UFormField label="Intro">
    <UTextarea
      v-model="block.data.subtitle"
      :rows="2"
      class="w-full"
    />
  </UFormField>

  <UFormField label="Layout">
    <USelect
      v-model="block.data.layout"
      :items="[
        { label: 'Price table (best for long lists)', value: 'table' },
        { label: 'Cards (a few highlighted services)', value: 'cards' }
      ]"
      class="w-full"
    />
  </UFormField>

  <UFormField
    label="Category"
    help="Show one category only, or leave on All."
  >
    <USelect
      v-model="block.data.category"
      :items="categories"
      class="w-full"
    />
  </UFormField>

  <UFormField
    label="Maximum rows"
    help="Leave empty to show everything."
  >
    <UInput
      :model-value="block.data.limit ?? undefined"
      type="number"
      min="1"
      max="200"
      class="w-full"
      @update:model-value="block.data.limit = $event === '' ? null : Number($event)"
    />
  </UFormField>

  <UCheckbox
    v-model="block.data.show_notes"
    label="Show notes (e.g. “1 нүүр”)"
  />

  <UCheckbox
    v-model="block.data.show_surcharges"
    label="Show surcharges (urgency, certification)"
  />

  <UCheckbox
    v-model="block.data.enable_ordering"
    label="Let visitors order from this list"
    help="Adds an Add button to each row and a checkout at /order. Needs at least one payment method configured."
  />

  <UFormField label="Button label">
    <UInput
      v-model="block.data.cta_label"
      class="w-full"
      placeholder="Үнийн санал авах"
    />
  </UFormField>

  <UFormField label="Button link">
    <UInput
      v-model="block.data.cta_url"
      class="w-full"
      placeholder="/quote"
    />
  </UFormField>
</template>
